import React, { useState, useEffect } from 'react';
import { Cloud } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const weatherIcon = (code) => {
  if (code === 0) return '☀️';
  if (code <= 3) return '⛅';
  if (code === 45 || code === 48) return '🌫️';
  if (code >= 51 && code <= 57) return '🌦️';
  if (code >= 61 && code <= 67) return '🌧️';
  if (code >= 71 && code <= 77) return '🌨️';
  if (code >= 80 && code <= 82) return '🌦️';
  if (code >= 95) return '⛈️';
  return '⛅';
};

const walkRec = (code, temp, wind) => {
  if (code >= 95) return { text: 'Not recommended today', color: 'text-red-600' };
  if (code >= 61 || (code >= 51 && code <= 57)) return { text: 'Rain expected, consider rescheduling', color: 'text-amber-600' };
  if (wind > 25) return { text: 'Very windy today', color: 'text-amber-600' };
  if (temp > 95) return { text: 'Very hot, go early morning or evening', color: 'text-amber-600' };
  if (temp < 45) return { text: 'Cold today, dress in layers', color: 'text-blue-600' };
  if (code === 0 && temp >= 55 && temp <= 85 && wind < 15) return { text: 'Perfect walking weather', color: 'text-green-600' };
  if (code <= 3 && temp >= 50 && temp <= 90) return { text: 'Good conditions, bring a layer', color: 'text-green-600' };
  if (code === 45 || code === 48) return { text: 'Comfortable walking, no sun glare', color: 'text-gray-600' };
  return { text: 'Good day for this walk', color: 'text-green-600' };
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WeatherWidget = ({ lat, lng }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!lat || !lng || lat === 0 || lng === 0) { setLoading(false); return; }
    const fetchWeather = async () => {
      try {
        const resp = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weathercode,windspeed_10m,precipitation&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_sum&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto&forecast_days=7`
        );
        if (resp.ok) setData(await resp.json());
      } catch {}
      setLoading(false);
    };
    fetchWeather();
  }, [lat, lng]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="h-8 bg-gray-200 rounded w-1/3" />
            <div className="h-3 bg-gray-200 rounded w-3/4" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data?.current || !data?.daily) return null;

  const current = data.current;
  const temp = Math.round(current.temperature_2m);
  const code = current.weathercode;
  const wind = Math.round(current.windspeed_10m);
  const rec = walkRec(code, temp, wind);

  const daily = data.daily;
  const forecast = daily.time.slice(1, 6).map((date, i) => ({
    day: DAYS[new Date(date + 'T12:00:00').getDay()],
    icon: weatherIcon(daily.weathercode[i + 1]),
    high: Math.round(daily.temperature_2m_max[i + 1]),
    low: Math.round(daily.temperature_2m_min[i + 1]),
  }));

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Cloud className="h-5 w-5" />
          Walking Weather
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Today */}
        <div className="flex items-center gap-3">
          <span className="text-3xl">{weatherIcon(code)}</span>
          <div>
            <p className="text-2xl font-bold text-gray-900">{temp}°F</p>
            <p className="text-xs text-gray-500">Wind {wind} mph</p>
          </div>
        </div>
        <p className={`text-sm font-medium ${rec.color}`}>{rec.text}</p>

        {/* 5-day forecast */}
        <div className="border-t border-gray-100 pt-3">
          <div className="grid grid-cols-5 gap-1 text-center">
            {forecast.map((d, i) => (
              <div key={i} className="space-y-0.5">
                <p className="text-[10px] font-medium text-gray-500">{d.day}</p>
                <p className="text-sm">{d.icon}</p>
                <p className="text-[10px] text-gray-700 font-medium">{d.high}°</p>
                <p className="text-[10px] text-gray-400">{d.low}°</p>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default WeatherWidget;
